import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Info, Loader2, RefreshCw, Search, Table2, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { R, fmtQty, pct, rangeToDates, StatTile, ExplainerBox, EmptyState, ErrorState, errMsg } from "./shared";

const RANGES = [
  { v: "7d", label: "Last 7 days" },
  { v: "30d", label: "Last 30 days" },
  { v: "this_month", label: "This month" },
  { v: "last_month", label: "Last month" },
];

const CLASS_META = {
  star: { label: "Star", short: "Popular · high margin", advice: "Protect it: keep quality consistent, feature it, never discount it.", tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
  plowhorse: { label: "Plowhorse", short: "Popular · low margin", advice: "Guests love it but it earns little per plate: trim portion cost, re-price carefully, or pair it with a high-margin side.", tone: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  puzzle: { label: "Puzzle", short: "Unpopular · high margin", advice: "Earns well but sells slowly: move it on the menu, rename, let staff recommend it, or try a small price drop.", tone: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30" },
  dog: { label: "Dog", short: "Unpopular · low margin", advice: "Sells little and earns little: rework the recipe, re-price, or consider removing it.", tone: "bg-destructive/15 text-destructive border-destructive/30" },
  unclassified: { label: "Uncosted", short: "No cost on file", advice: "Add a recipe or type a cost on the item so it can be judged.", tone: "bg-secondary text-muted-foreground border-border" },
};

function rangeParams(key) {
  if (key === "this_month") {
    const d = new Date();
    const s = new Date(d.getFullYear(), d.getMonth(), 1);
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    return { start_date: iso(s), end_date: iso(d) };
  }
  if (key === "last_month") {
    const d = new Date();
    const s = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const e = new Date(d.getFullYear(), d.getMonth(), 0);
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    return { start_date: iso(s), end_date: iso(e) };
  }
  return rangeToDates(key);
}

const COLUMNS = [
  { key: "name", label: "Menu item", align: "left" },
  { key: "quantity_sold", label: "Sold", align: "right", hint: "Units sold in the period (non-voided lines)." },
  { key: "revenue", label: "Revenue", align: "right", hint: "Line totals, VAT and refunds treated exactly as in Expenses & Profit." },
  { key: "unit_cost", label: "Cost / unit", align: "right", hint: "Current recipe or typed item cost.", hide: "lg" },
  { key: "cost", label: "Cost", align: "right", hint: "Sold × cost per unit.", hide: "md" },
  { key: "margin", label: "Margin", align: "right", hint: "Revenue − cost. This is the item's contribution." },
  { key: "margin_per_unit", label: "Margin / unit", align: "right", hint: "Margin ÷ sold. Compared against the margin cut-off.", hide: "sm" },
  { key: "margin_pct", label: "Margin %", align: "right", hint: "Margin ÷ revenue.", hide: "md" },
  { key: "share_of_margin_pct", label: "Share of margin", align: "right", hint: "This item's slice of total margin.", hide: "lg" },
  { key: "classification", label: "Label", align: "left" },
];

/**
 * Session 38 — Reports → Menu Engineering.
 * Transparent first: a sortable table of the raw Sales × Margin numbers per
 * item. The classic quadrant labels sit on top, and the two cut-offs that
 * produced them are always visible so the merchant can audit every label.
 */
export const MenuEngineeringPanel = ({ http, authHeaders, onSendToSpreadsheet }) => {
  const [range, setRange] = useState("30d");
  const [category, setCategory] = useState("all");
  const [view, setView] = useState("table");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "margin", dir: "desc" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (rangeKey) => {
    setLoading(true);
    setError("");
    try {
      const r = await http.get("/owner/menu-engineering", { headers: authHeaders, params: rangeParams(rangeKey || range) });
      setData(r.data);
    } catch (e) {
      setError(errMsg(e, "Unable to load menu engineering."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders, range]);

  // Mount-only load (pickers/range changes call load() themselves).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(range); }, []);

  const categories = useMemo(() => Array.from(new Set((data?.items || []).map((i) => i.category).filter(Boolean))).sort(), [data]);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = (data?.items || []).filter((i) => (category === "all" || i.category === category) && (!q || String(i.name || "").toLowerCase().includes(q)));
    const dir = sort.dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const av = a[sort.key]; const bv = b[sort.key];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return list;
  }, [data, category, query, sort]);

  const t = data?.totals || {};
  const cut = data?.cutoffs || {};
  const counts = data?.counts || {};

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: key === "name" || key === "classification" ? "asc" : "desc" }));

  const quadrant = (cls) => items.filter((i) => i.classification === cls);

  const exportRows = () => {
    if (!data) return;
    const rows = [["Item", "Category", "Sold", "Revenue (R)", "Cost/unit (R)", "Cost (R)", "Margin (R)", "Margin/unit (R)", "Margin %", "Share of margin %", "Label", "Why"]];
    items.forEach((i) => rows.push([i.name, i.category || "", i.quantity_sold, i.revenue, i.unit_cost, i.cost, i.margin, i.margin_per_unit, i.margin_pct, i.share_of_margin_pct, CLASS_META[i.classification]?.label || i.classification, i.classification_reason || ""]));
    rows.push([]);
    rows.push(["Popularity cut-off (units)", cut.popularity_qty], ["Margin cut-off (R/unit)", cut.margin_per_unit], ["Costed items", cut.costed_items]);
    onSendToSpreadsheet?.(rows, `Menu engineering ${data.range?.start?.slice(0, 10) || ""}`);
  };

  return (
    <div className="space-y-3" data-testid="menu-engineering-panel">
      <ExplainerBox title="How to read this" testId="menu-engineering-explainer">
        <p>Every menu item is measured on two things you can verify: <strong>how many you sold</strong> and <strong>how much margin each one earns</strong> (selling price less its current recipe cost). The table shows the raw numbers; the labels are just those two numbers compared against the cut-offs shown below.</p>
      </ExplainerBox>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-2" data-testid="menu-engineering-range-picker">
          {RANGES.map((r) => <Button key={r.v} type="button" size="sm" variant={range === r.v ? "default" : "outline"} className="rounded-xl" onClick={() => { setRange(r.v); load(r.v); }} data-testid={`menu-engineering-range-${r.v}`}>{r.label}</Button>)}
        </div>
        {categories.length ? (
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 w-44 rounded-xl" data-testid="menu-engineering-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : null}
        <Tabs value={view} onValueChange={setView} className="sm:ml-auto">
          <TabsList className="h-9 rounded-xl">
            <TabsTrigger value="table" className="rounded-lg text-xs" data-testid="menu-engineering-view-table"><Table2 className="mr-1 h-3.5 w-3.5" />Numbers</TabsTrigger>
            <TabsTrigger value="quadrants" className="rounded-lg text-xs" data-testid="menu-engineering-view-quadrants"><LayoutGrid className="mr-1 h-3.5 w-3.5" />Quadrants</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={() => load(range)} disabled={loading} data-testid="menu-engineering-refresh">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</Button>
        {onSendToSpreadsheet ? <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={exportRows} disabled={!data} data-testid="menu-engineering-export">To spreadsheet</Button> : null}
      </div>

      {error ? <ErrorState message={error} onRetry={() => load(range)} testId="menu-engineering-error" /> : null}

      {loading && !data ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Revenue (costed + uncosted)" value={R(t.revenue)} hint={`${t.orders_counted} orders · ${fmtQty(t.quantity_sold)} units · ${data.settings?.profit_basis === "excl_vat" ? "excl. VAT" : "incl. VAT"}`} testId="menu-engineering-tile-revenue" />
            <StatTile label="Cost of costed items" value={R(t.cost)} hint="Sold × current cost per unit" tone="muted" testId="menu-engineering-tile-cost" />
            <StatTile label="Margin of costed items" value={R(t.margin)} hint={t.margin_pct !== null && t.margin_pct !== undefined ? `${pct(t.margin_pct)} of costed revenue` : "—"} tone="good" testId="menu-engineering-tile-margin" />
            <StatTile label="Uncosted revenue" value={R(t.uncosted_revenue)} hint={`${t.uncosted_items || 0} item(s) have no cost — excluded from margin`} tone={Number(t.uncosted_revenue) > 0 ? "warn" : "good"} testId="menu-engineering-tile-uncosted" />
          </div>

          <Card className="rounded-2xl border-border bg-secondary/40" data-testid="menu-engineering-cutoffs">
            <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 p-3 text-xs">
              <span className="font-semibold uppercase tracking-wide text-muted-foreground">Cut-offs used</span>
              <span><strong>Popular</strong> = sold ≥ <span className="tabular-nums font-semibold" data-testid="cutoff-popularity">{fmtQty(cut.popularity_qty)}</span> units <span className="text-muted-foreground">(70% of the fair share {fmtQty(cut.fair_share_qty)} = costed units ÷ {cut.costed_items} costed items)</span></span>
              <span><strong>High margin</strong> = margin/unit ≥ <span className="tabular-nums font-semibold" data-testid="cutoff-margin">{R(cut.margin_per_unit)}</span> <span className="text-muted-foreground">(total margin ÷ costed units)</span></span>
              <span className="flex flex-wrap gap-1.5">
                {["star", "plowhorse", "puzzle", "dog", "unclassified"].map((k) => <Badge key={k} variant="outline" className={`rounded-full ${CLASS_META[k].tone}`} data-testid={`menu-engineering-count-${k}`}>{CLASS_META[k].label} {counts[k] || 0}</Badge>)}
              </span>
            </CardContent>
          </Card>
        </>
      ) : null}

      {view === "table" ? (
        <Card className="rounded-2xl border-border bg-card" data-testid="menu-engineering-table-card">
          <CardContent className="p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold">Sales × Margin · per item</p>
              <div className="relative w-full sm:w-56">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input type="search" placeholder="Find item…" value={query} onChange={(e) => setQuery(e.target.value)} className="h-9 w-full rounded-xl pl-8" data-testid="menu-engineering-search" />
              </div>
            </div>
            {loading && !data ? <Skeleton className="h-48 w-full rounded-2xl" /> : items.length ? (
              <TooltipProvider delayDuration={200}>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {COLUMNS.map((c) => (
                          <TableHead key={c.key} className={`${c.align === "right" ? "text-right" : ""} ${c.hide ? `hidden ${c.hide}:table-cell` : ""}`}>
                            <button type="button" onClick={() => toggleSort(c.key)} className={`inline-flex items-center gap-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${sort.key === c.key ? "text-foreground" : ""}`} data-testid={`menu-engineering-sort-${c.key}`}>
                              {c.label}
                              {sort.key === c.key ? (sort.dir === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                              {c.hint ? <Tooltip><TooltipTrigger asChild><Info className="h-3 w-3 opacity-50" /></TooltipTrigger><TooltipContent className="max-w-xs text-xs">{c.hint}</TooltipContent></Tooltip> : null}
                            </button>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((i) => {
                        const meta = CLASS_META[i.classification] || CLASS_META.unclassified;
                        const mTone = i.margin === null || i.margin === undefined ? "text-muted-foreground" : i.margin < 0 ? "text-destructive font-semibold" : "font-semibold";
                        return (
                          <TableRow key={i.id} data-testid={`menu-engineering-row-${i.id}`}>
                            <TableCell className="font-medium">
                              <span className="block truncate">{i.name}</span>
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{i.category || "—"}{i.cost_status && i.cost_status !== "full" ? ` · ${i.cost_status}` : ""}</span>
                            </TableCell>
                            <TableCell className="text-right text-xs tabular-nums">{fmtQty(i.quantity_sold)}{i.share_of_quantity_pct !== null && i.share_of_quantity_pct !== undefined ? <span className="block text-[10px] text-muted-foreground">{pct(i.share_of_quantity_pct)}</span> : null}</TableCell>
                            <TableCell className="text-right text-xs tabular-nums">{R(i.revenue)}</TableCell>
                            <TableCell className="hidden text-right text-xs tabular-nums lg:table-cell">{i.unit_cost !== null && i.unit_cost !== undefined ? R(i.unit_cost) : <span className="text-muted-foreground">—</span>}</TableCell>
                            <TableCell className="hidden text-right text-xs tabular-nums md:table-cell">{R(i.cost)}</TableCell>
                            <TableCell className={`text-right text-xs tabular-nums ${mTone}`}>{R(i.margin)}</TableCell>
                            <TableCell className={`hidden text-right text-xs tabular-nums sm:table-cell ${i.margin_per_unit !== null && i.margin_per_unit !== undefined && cut.margin_per_unit !== undefined ? (i.margin_per_unit >= cut.margin_per_unit ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400") : ""}`}>{R(i.margin_per_unit)}</TableCell>
                            <TableCell className="hidden text-right text-xs tabular-nums md:table-cell">{pct(i.margin_pct)}</TableCell>
                            <TableCell className="hidden text-right text-xs tabular-nums lg:table-cell">{pct(i.share_of_margin_pct)}</TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger asChild><Badge variant="outline" className={`cursor-help rounded-full ${meta.tone}`} data-testid={`menu-engineering-label-${i.id}`}>{meta.label}</Badge></TooltipTrigger>
                                <TooltipContent className="max-w-sm text-xs"><p className="font-medium">{meta.short}</p><p className="mt-1">{i.classification_reason}</p></TooltipContent>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </TooltipProvider>
            ) : data ? <EmptyState title="No sales in this period" body="Items appear here as soon as orders are placed." testId="menu-engineering-empty" /> : null}
            {data?.method ? <p className="mt-3 text-[11px] text-muted-foreground" data-testid="menu-engineering-method">Method: {data.method}</p> : null}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="menu-engineering-quadrants">
          {["star", "plowhorse", "puzzle", "dog"].map((k) => {
            const meta = CLASS_META[k];
            const list = quadrant(k);
            return (
              <Card key={k} className="rounded-2xl border-border bg-card" data-testid={`menu-engineering-quadrant-${k}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Badge variant="outline" className={`rounded-full ${meta.tone}`}>{meta.label} · {list.length}</Badge>
                      <p className="mt-1 text-xs text-muted-foreground">{meta.short}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-xs">{meta.advice}</p>
                  {list.length ? (
                    <ul className="mt-3 space-y-1.5">
                      {list.map((i) => (
                        <li key={i.id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-xs" data-testid={`menu-engineering-quadrant-item-${i.id}`}>
                          <span className="min-w-0 truncate font-medium">{i.name}</span>
                          <span className="shrink-0 tabular-nums text-muted-foreground">{fmtQty(i.quantity_sold)} sold · {R(i.margin_per_unit)}/unit · {R(i.margin)} total</span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="mt-3 text-xs text-muted-foreground">Nothing in this quadrant for the period.</p>}
                </CardContent>
              </Card>
            );
          })}
          {quadrant("unclassified").length ? (
            <Card className="rounded-2xl border-amber-500/40 bg-amber-500/10 md:col-span-2" data-testid="menu-engineering-quadrant-unclassified">
              <CardContent className="p-4">
                <p className="text-sm font-semibold">{quadrant("unclassified").length} item(s) can't be judged — no cost on file</p>
                <p className="mt-1 text-xs text-muted-foreground">{R(t.uncosted_revenue)} of revenue is excluded from every margin number above. Add recipes (Inventory → Recipes) or type a cost on the item.</p>
                <div className="mt-2 flex flex-wrap gap-1.5">{quadrant("unclassified").map((i) => <Badge key={i.id} variant="outline" className="rounded-full">{i.name} · {fmtQty(i.quantity_sold)} sold · {R(i.revenue)}</Badge>)}</div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
};
